/* eslint-disable no-continue */
import {
  findDependents,
  PartialQuestionnaire,
  QuestionnaireQuestions,
  Rule,
} from '@getpopsure/qnr-framework';

import { getNestedValue } from '.';

export type RemoveAnswerType<Questionnaire extends QuestionnaireQuestions> = {
  op: 'always';
  questions: Array<keyof Questionnaire>;
};

export const nestedQuestionsStateManagerHelper = <
  Questionnaire extends QuestionnaireQuestions,
  GroupId,
  ExtendedComponentTypes
>(
  config: Partial<Record<keyof Questionnaire, RemoveAnswerType<Questionnaire>>>,
  questionnaire: PartialQuestionnaire<
    Questionnaire,
    GroupId,
    ExtendedComponentTypes
  >,
  answers: Partial<Questionnaire>,
  rules: Rule<Questionnaire>[]
) => {
  const state = { ...answers };
  const manager = {
    getNestedAnswersToRemove: <QuestionId extends keyof Questionnaire>(
      questionId: QuestionId,
      nestedQuestionIds: QuestionId[][] = [],
      answer: Questionnaire[QuestionId]
    ) => {
      if (!nestedQuestionIds?.length) {
        return [];
      }

      let removableAnswers: (keyof Questionnaire)[] = [];
      const answersToRemove = config[questionId]?.questions || [];
      if (answersToRemove !== undefined) {
        removableAnswers = answersToRemove;
      }

      nestedQuestionIds.forEach((nestedQuestionId: QuestionId[]) => {
        const lastNestedQuestionId =
          nestedQuestionId?.[nestedQuestionId.length - 1];

        const previousAnswer = state[lastNestedQuestionId];
        const nestedAnswer = getNestedValue(
          answer,
          nestedQuestionId as string[]
        );

        const hasChange =
          JSON.stringify(nestedAnswer) !== JSON.stringify(previousAnswer);

        if (!hasChange) {
          return [];
        }

        answersToRemove.push(
          ...findDependents(questionnaire, rules, questionId)
        );
      });

      return [...new Set([...removableAnswers, ...(answersToRemove || [])])];
    },
  };

  return manager;
};
